/**
 * Public Signing Page Component
 * Unauthenticated page for external signers.
 * Shows document with fields to fill, requires scroll-through before signing.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { SignaturePad, SignatureResult } from '@/components/signatures/SignaturePad';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { config as productConfig } from '@/src/config';
import {
  CheckCircle, AlertTriangle, Clock, FileText, Shield, PenTool,
  Calendar, Type, Hash, ChevronDown,
} from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = (productConfig.weaveinApi.baseUrl ?? '').trim();

interface SigningField {
  id: string;
  type: 'signature' | 'date' | 'initials' | 'text' | 'checkbox';
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  label?: string;
}

interface SigningInfo {
  id: number;
  uuid: string;
  title: string;
  message: string | null;
  signer_name: string | null;
  signer_email: string | null;
  signer_role: string | null;
  status: string;
  signed: boolean;
  document_url: string | null;
  document_file_name: string | null;
  document_mime_type: string | null;
  signing_fields: SigningField[];
}

interface PublicSigningPageProps {
  token: string;
}

export function PublicSigningPage({ token }: PublicSigningPageProps) {

  const [signingInfo, setSigningInfo] = useState<SigningInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // PDF state
  const [numPages, setNumPages] = useState(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Field values
  const [filledFields, setFilledFields] = useState<Record<string, any>>({});
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [sigPadOpen, setSigPadOpen] = useState(false);
  const [sigPadFieldId, setSigPadFieldId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/signatures/sign/${token}`)
      .then((r) => r.json())
      .then((data) => {
        setSigningInfo(data);
        if (data.signed) setSigned(true);
        if (!data.document_url) setHasScrolledToBottom(true);
        // Auto-fill profile-bound fields
        const autoFilled: Record<string, any> = {};
        (data.signing_fields || []).forEach((f: any) => {
          if (f.default_value) autoFilled[f.id] = f.default_value;
        });
        if (Object.keys(autoFilled).length > 0) setFilledFields(autoFilled);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Scroll tracking
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || hasScrolledToBottom) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  // Check if all required fields are filled
  const allRequiredFilled = () => {
    if (!signingInfo?.signing_fields?.length) return true;
    return signingInfo.signing_fields
      .filter(f => f.required)
      .every(f => filledFields[f.id]);
  };

  const canSign = hasScrolledToBottom && allRequiredFilled();

  // Handle field click
  const handleFieldClick = (field: SigningField) => {
    if (field.type === 'signature' || field.type === 'initials') {
      setSigPadFieldId(field.id);
      setSigPadOpen(true);
    } else if (field.type === 'date') {
      setFilledFields({ ...filledFields, [field.id]: new Date().toLocaleDateString() });
    } else if (field.type === 'checkbox') {
      setFilledFields({ ...filledFields, [field.id]: !filledFields[field.id] });
    } else {
      setActiveFieldId(field.id);
    }
  };

  const handleSign = async (result: SignatureResult) => {
    setSubmitting(true);
    try {
      const body = {
        ...result,
        filled_fields: filledFields,
      };
      const res = await fetch(`${API_URL}/api/signatures/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Signing failed');
      }
      setSigned(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Render states
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error && !signingInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-red-700">Error</h2>
            <p className="text-muted-foreground mt-2">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!signingInfo || signingInfo.status === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Clock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold">Request Expired</h2>
            <p className="text-muted-foreground mt-2">Please contact the sender for a new link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed || signingInfo.signed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-green-700">Signed Successfully</h2>
            <p className="text-muted-foreground mt-2">
              Thank you for signing <strong>{signingInfo.title}</strong>
            </p>
            <p className="text-xs text-muted-foreground mt-4">You may close this window.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fields = signingInfo.signing_fields || [];

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4">
          <Shield className="w-8 h-8 text-blue-600 mx-auto mb-1" />
          <h1 className="text-lg font-bold">Trust Marketplace</h1>
          <p className="text-xs text-muted-foreground">Secure Electronic Signature</p>
        </div>

        {/* Signer identity banner */}
        {signingInfo.signer_name && (
          <div className="flex items-center gap-3 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
              {signingInfo.signer_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>
            <div>
              <div className="font-semibold text-sm">{signingInfo.signer_name}</div>
              <div className="text-xs text-muted-foreground">
                {signingInfo.signer_email && <span>{signingInfo.signer_email} · </span>}
                {signingInfo.signer_role && <span className="capitalize">{signingInfo.signer_role}</span>}
                {(signingInfo as any).signer_order && (signingInfo as any).signer_order > 1 && (
                  <span> · Signer {(signingInfo as any).signer_order}</span>
                )}
              </div>
            </div>
          </div>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-5 h-5" />
              {signingInfo.title}
            </CardTitle>
            {signingInfo.message && (
              <p className="text-sm text-muted-foreground">{signingInfo.message}</p>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Document Viewer */}
            {signingInfo.document_url ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium flex items-center gap-1">
                    <FileText className="w-3 h-3" /> {signingInfo.document_file_name}
                  </span>
                  {!hasScrolledToBottom && (
                    <Badge variant="secondary" className="text-xs flex items-center gap-1">
                      <ChevronDown className="w-3 h-3 animate-bounce" /> Scroll to continue
                    </Badge>
                  )}
                </div>

                <div
                  ref={scrollRef}
                  className="border rounded-lg overflow-y-auto bg-gray-100"
                  style={{ maxHeight: '500px' }}
                  onScroll={handleScroll}
                >
                  {signingInfo.document_mime_type?.startsWith('image/') ? (
                    /* Image document */
                    <div className="relative">
                      <img src={signingInfo.document_url!} alt="Document" style={{ width: '100%' }} onLoad={() => { setNumPages(1); setHasScrolledToBottom(true); }} />
                      {/* Field overlays for image */}
                      {fields.map(field => {
                        const isFilled = !!filledFields[field.id];
                        return (
                          <div key={field.id} className={`absolute border-2 rounded cursor-pointer transition-all ${isFilled ? 'border-green-500 bg-green-50/80' : field.required ? 'border-red-400 bg-red-50/60 animate-pulse' : 'border-blue-400 bg-blue-50/60'}`}
                            style={{ left: `${field.x}%`, top: `${field.y}%`, width: `${field.width}%`, height: `${field.height}%` }}
                            onClick={() => handleFieldClick(field)}>
                            {isFilled ? (
                              <div className="w-full h-full flex items-center justify-center overflow-hidden p-0.5">
                                {(field.type === 'signature' || field.type === 'initials') && filledFields[field.id]?.startsWith?.('data:image') ? (
                                  <img src={filledFields[field.id]} alt="Signature" className="max-h-full max-w-full object-contain" />
                                ) : field.type === 'checkbox' ? (<CheckCircle className="w-4 h-4 text-green-600" />) : (
                                  <span className="text-xs text-green-700 truncate">{filledFields[field.id]}</span>
                                )}
                              </div>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center gap-1 text-xs">
                                {field.type === 'signature' && <><PenTool className="w-3 h-3" /> Sign</>}
                                {field.type === 'date' && <><Calendar className="w-3 h-3" /> Date</>}
                                {field.type === 'initials' && <><Hash className="w-3 h-3" /> Init</>}
                                {field.type === 'text' && <><Type className="w-3 h-3" /> Text</>}
                                {field.type === 'checkbox' && <>Check</>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                  <Document
                    file={signingInfo.document_url}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    onLoadError={(err) => { console.error('[PublicSigning] PDF load error:', err); setHasScrolledToBottom(true); }}
                    loading={
                      <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                      </div>
                    }
                  >
                    {Array.from({ length: numPages }, (_, i) => (
                      <div key={i + 1} className="relative mb-2">
                        <Page
                          pageNumber={i + 1}
                          width={Math.min(650, typeof window !== 'undefined' ? window.innerWidth - 80 : 650)}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                        {/* Field overlays for this page */}
                        {fields.filter(f => f.page === i + 1).map(field => {
                          const isFilled = !!filledFields[field.id];
                          return (
                            <div
                              key={field.id}
                              className={`absolute border-2 rounded cursor-pointer transition-all ${
                                isFilled
                                  ? 'border-green-500 bg-green-50/80'
                                  : field.required
                                    ? 'border-red-400 bg-red-50/60 animate-pulse'
                                    : 'border-blue-400 bg-blue-50/60'
                              }`}
                              style={{
                                left: `${field.x}%`,
                                top: `${field.y}%`,
                                width: `${field.width}%`,
                                height: `${field.height}%`,
                              }}
                              onClick={() => handleFieldClick(field)}
                            >
                              {isFilled ? (
                                <div className="w-full h-full flex items-center justify-center overflow-hidden p-0.5">
                                  {(field.type === 'signature' || field.type === 'initials') && filledFields[field.id]?.startsWith?.('data:image') ? (
                                    <img src={filledFields[field.id]} alt="Signature" className="max-h-full max-w-full object-contain" />
                                  ) : field.type === 'checkbox' ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <span className="text-xs text-green-700 truncate">{filledFields[field.id]}</span>
                                  )}
                                </div>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center gap-1 text-xs">
                                  {field.type === 'signature' && <><PenTool className="w-3 h-3" /> Sign</>}
                                  {field.type === 'date' && <><Calendar className="w-3 h-3" /> Date</>}
                                  {field.type === 'initials' && <><Hash className="w-3 h-3" /> Init</>}
                                  {field.type === 'text' && <><Type className="w-3 h-3" /> Text</>}
                                  {field.type === 'checkbox' && <>Check</>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </Document>
                  )}
                </div>

                {/* Text input for active text field */}
                {activeFieldId && fields.find(f => f.id === activeFieldId)?.type === 'text' && (
                  <div className="flex gap-2 mt-2">
                    <Input
                      autoFocus
                      placeholder={fields.find(f => f.id === activeFieldId)?.label || 'Enter text'}
                      value={filledFields[activeFieldId] || ''}
                      onChange={(e) => setFilledFields({ ...filledFields, [activeFieldId]: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') setActiveFieldId(null); }}
                    />
                    <Button size="sm" onClick={() => setActiveFieldId(null)}>Done</Button>
                  </div>
                )}
              </div>
            ) : (
              /* No document — just show signature pad directly */
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground mb-4">
                  No document attached. Please sign below to confirm.
                </p>
              </div>
            )}

            {/* Field status */}
            {fields.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {fields.map(f => (
                  <Badge
                    key={f.id}
                    variant={filledFields[f.id] ? 'default' : 'outline'}
                    className={`text-xs cursor-pointer ${filledFields[f.id] ? 'bg-green-100 text-green-800' : f.required ? 'border-red-300 text-red-600' : ''}`}
                    onClick={() => handleFieldClick(f)}
                  >
                    {filledFields[f.id] ? <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> : null}
                    {f.label || f.type} {f.required && !filledFields[f.id] && '*'}
                  </Badge>
                ))}
              </div>
            )}

            {/* Sign section */}
            {!signingInfo.document_url || hasScrolledToBottom ? (
              <div>
                {!canSign && fields.length > 0 && (
                  <p className="text-xs text-amber-600 mb-2">Please fill all required fields before signing.</p>
                )}
                <SignaturePad
                  signerName={signingInfo.signer_name || ''}
                  title="Sign Below"
                  message={canSign ? 'All fields complete. Sign to finalize.' : 'Complete all required fields above, then sign.'}
                  onSign={handleSign}
                  disabled={!canSign || submitting}
                />
              </div>
            ) : (
              <div className="text-center py-2">
                <p className="text-sm text-muted-foreground">
                  Please scroll through the entire document to enable signing.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-xs text-gray-400">Powered by Trust Marketplace | ESIGN Act Compliant</p>
        </div>
      </div>

      {/* Signature Pad Dialog for fields */}
      <Dialog open={sigPadOpen} onOpenChange={setSigPadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {sigPadFieldId && fields.find(f => f.id === sigPadFieldId)?.type === 'initials' ? 'Add Initials' : 'Add Signature'}
            </DialogTitle>
          </DialogHeader>
          <SignaturePad
            signerName={signingInfo?.signer_name || ''}
            title={sigPadFieldId && fields.find(f => f.id === sigPadFieldId)?.type === 'initials' ? 'Initials' : 'Signature'}
            onSign={(result) => {
              if (sigPadFieldId) {
                setFilledFields({ ...filledFields, [sigPadFieldId]: result.signature_data });
              }
              setSigPadOpen(false);
              setSigPadFieldId(null);
            }}
            onCancel={() => { setSigPadOpen(false); setSigPadFieldId(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
